import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useContext, useEffect, useState } from 'react'
import { ShowVehicleQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { ArrowsRightLeftIcon, CreditCardIcon, ScaleIcon, TruckIcon } from '@heroicons/react/24/solid'
import { Accordion } from '@/components/accordion'
import { Carousel } from '../carousel'
import { CurrentTenantContext } from '@/core/current_tenant_context'
import { formatCurrency } from '@/helpers/formatCurrency'
import { Text } from '@/components/base/text'
import { Table as ProductSpecificationTable } from '@/components/product_specification/table'
import { Table as MultipliersTable } from '@/components/product_multiplier_type/table'
import { useLocale } from '@/helpers/useLocale'
import { Button } from '../base/button'
import { EditDialog } from './edit_dialog'
import { DeleteDialog } from '../delete_dialog'
import { Query as TableQuery } from './table_query'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'
import { AttachmentResourceType } from '@/types'

type Vehicle = ShowVehicleQuery['vehicles']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const { data, loading, error } = useQuery<ShowVehicleQuery>(Query, { variables: { filters: { idEq: id } } })
  const { currentCompany } = useContext(CurrentTenantContext)
  const { units } = useLocale(currentCompany?.locale)

  useEffect(() => {
    if (!data || !id) { return }

    setVehicle(data.vehicles.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  const onDelete = () => {
    setOpenDelete(true)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          {
            loading || !vehicle
              ? <Loading />
              : <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { vehicle.name }
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <CreditCardIcon className="h-6 w-6" />
                    <Subheading>Base Rate</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', vehicle.rateCents) }</Text>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <TruckIcon className="h-6 w-6" />
                    <Subheading>Mileage rate</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', vehicle.mileageCents) }</Text>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <ArrowsRightLeftIcon className="h-6 w-6" />
                    <Subheading>Length</Subheading>
                    <Text>{ vehicle.length + units.dimension }</Text>
                    <Subheading>Width</Subheading>
                    <Text>{ vehicle.width + units.dimension}</Text>
                    <Subheading>Height</Subheading>
                    <Text>{ vehicle.height + units.dimension}</Text>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <ScaleIcon className="h-6 w-6" />
                    <Subheading>Payload</Subheading>
                    <Text>{ vehicle.payload + units.weight}</Text>
                  </span>
                  { vehicle.notes && (
                    <span className="flex flex-row gap-2 items-center pt-6">
                      <Text>{vehicle.notes}</Text>
                    </span>
                  )}
                </div>
              </div>
          }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { vehicle && <div className="flex flex-col gap-4">
            <Carousel images={vehicle.images} />
            <Divider />
            <Accordion title="Specifications">
              <ProductSpecificationTable filters={{ productIdEq: vehicle.id }} fixedHeight />
            </Accordion>
            <Accordion title="Multipliers">
              <MultipliersTable filters={{ productIdEq: vehicle.id }} fixedHeight />
            </Accordion>
            <Accordion title="Attachments">
              <AttachmentsCollection
                filters={{ nameEq: 'attachments', resourceIdEq: vehicle.id, resourceTypeEq: AttachmentResourceType.Vehicle }}
                pageSize={10}
                fixedHeight
              />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { vehicle && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { vehicle && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { vehicle && <EditDialog id={vehicle.id} open={openEdit} setOpen={setOpenEdit} /> }
      { vehicle && <DeleteDialog
        id={vehicle.id}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[TableQuery]}
        setOpen={setOpenDelete}
        onSuccess={onDeleteSuccess}
      /> }
    </>
  )
}
